(async () => {
  const {
    loadScript,
    loadStyle
  } = await import(window.theme.modules.utils);

  const baseComponent = await import(window.theme.modules.baseComponent);

  const isChromeForIOs145 = () => {
    const userAgent = window.navigator.userAgent;
    const isChromeForIOs = /CriOS/i.test(userAgent);
    if (isChromeForIOs) {
      const iOsMatch = userAgent.match(
        /(.+)(iPhone|iPad|iPod)(.+)OS[\s|\_](\d+)\_?(\d+)?[\_]?(\d+)?.+/i
      );
      if (iOsMatch && iOsMatch.length >= 6) {
        const iOsVersionMajor = parseInt(iOsMatch[4], 10);
        const iOsVersionMinor = parseInt(iOsMatch[5], 10);
        if (iOsVersionMajor >= 14 && iOsVersionMinor >= 6) {
          return true;
        }
      }
    }
    return false;
  }

  customElements.define('slide-show', class extends baseComponent.default {
    elements = {
      sliderContainer: '[data-slider-container]',
      slides: '*[data-slide-variants]',
      nextButton: '[data-slider-button-next]',
      prevButton: '[data-slider-button-prev]',
      pagination: '[data-slider-pagination]',
      thumbnails: 'slideshow-thumbnails'
    }


    async render() {
      await this._initSwiper();

      const delay = this.getAttribute('autoplay-delay') * 1000;
      const autoplay = this.hasAttribute('use-autoplay') && {delay};
      const navType = this.getAttribute('navigation-type');
      const usePagination = navType === 'pagination' || navType === 'pagination_buttons';
      const useButtons = navType === 'buttons' || navType === 'pagination_buttons';

      const swiperParams = {
        delay,
        autoplay,
        pagination: usePagination && {el: this.$pagination, clickable: true},
        navigation: useButtons && {
          nextEl: this.$nextButton,
          prevEl: this.$prevButton
        },
        zoom: this.hasAttribute('zoom'),
        breakpoints: this.hasAttribute('desktop-no-swipe') && {
          992: {
            allowTouchMove: false
          }
        },
        // Only block swiping on controls that need a drag of their own. Marking a whole
        // video/3d slide as no-swiping traps the user on it — they can't swipe past.
        // This is also what keeps those controls usable at all: Swiper returns early on a
        // match, before the preventDefault() on pointerdown that otherwise stops the
        // interaction reaching them. video-player[armed] is a native embed the viewer has
        // tapped into, so the provider owns that surface until the slide is left.
        noSwipingSelector: '.swiper-no-swiping, .plyr__controls, model-viewer, video-player[armed]',
        // Plyr and model-viewer build themselves after the slider is up, so let Swiper
        // re-measure when a slide's contents change instead of keeping stale sizes.
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        cssMode: isChromeForIOs145()
      }

      // This slider's own dot pager (slider.liquid `pager`), found inside it rather than
      // anywhere on the page, so other sliders never pick it up.
      const pagerSwiper = this._initPager();
      if (pagerSwiper) {
        swiperParams.thumbs = {swiper: pagerSwiper};
      }

      if (this.hasAttribute('sync-slider')) {
        this.syncSlider = document.getElementById(this.getAttribute('sync-slider'));
        swiperParams.initialSlide = this.syncSlider.getCurrentSlide();
      } else if (this.hasAttribute('initial-slide')) {
        // Start there rather than sliding there after init, which showed slide 1 first.
        swiperParams.initialSlide = +this.getAttribute('initial-slide') || 0;
      }

      this.swiper = new window.Swiper(this.$sliderContainer, swiperParams);

      if (pagerSwiper) this._bindPagerButtons();

      if (this.hasAttribute('observe-slide-type')) {
        this._setMediaType(this.swiper.activeIndex);
        this.swiper.on('slideChange', ({ activeIndex }) => {
          this._setMediaType(activeIndex);
        })
      }

      if(this.hasAttribute('zoom')) {
        this.swiper.on('zoomChange', (_, scale) => {
          scale === 1 ? this.removeAttribute('zoomed') : this.setAttribute('zoomed', '');
        })
      }

      if (this.syncSlider) {
        this.swiper.on('slideChange', () => {
          this.syncSlider.slideTo(this.getCurrentSlide());
        })

        if (this.hasAttribute('initial-slide')) {
          this.slideTo(+this.getAttribute('initial-slide'))
        }
      }

      if (this.hasAttribute('thumbnails') && this.$thumbnails) {
        // Its module may not have defined the element yet, and onSliderLoad is on its class.
        await customElements.whenDefined('slideshow-thumbnails');
        this.$thumbnails.onSliderLoad(this);
      }
    };

    async _initSwiper() {
      await loadScript(window.theme.scripts.Swiper, 'swiper');
      loadStyle(window.theme.styles.Swiper);
    };

    _initPager() {
      this.$pager = this.querySelector(':scope > [data-slider-pager]');
      const track = this.$pager && this.$pager.querySelector('[data-slider-pager-track]');
      if (!track) return null;
      return new window.Swiper(track, {
        slidesPerView: 4,
        spaceBetween: 8,
        allowTouchMove: false,
        watchSlidesProgress: true
      });
    }

    // The arrows used to page the dots, so they stopped 4 short of the last slide (the dots'
    // own last page). Driving the slider instead reaches every slide, and the dots follow.
    _bindPagerButtons() {
      const prev = this.$pager.querySelector('[data-slider-pager-prev]');
      const next = this.$pager.querySelector('[data-slider-pager-next]');
      prev && prev.addEventListener('click', () => this.swiper.slidePrev());
      next && next.addEventListener('click', () => this.swiper.slideNext());
      const sync = () => {
        if (prev) prev.disabled = this.swiper.isBeginning;
        if (next) next.disabled = this.swiper.isEnd;
      };
      sync();
      this.swiper.on('slideChange toEdge fromEdge lock unlock', sync);
    }

    getCurrentSlide() {
      return this.swiper.activeIndex;
    }

    slideTo(index) {
      // Swiper may still be loading: start there once it is up instead of throwing.
      if (!this.swiper) {
        this.setAttribute('initial-slide', index);
        return;
      }
      this.swiper.slideTo(index);
    }

    _setMediaType(slideIndex) {
      // Guard: throwing here would abort Swiper's event chain mid-emit and leave the
      // navigation buttons stuck in a stale enabled/disabled state.
      const slide = this.swiper.slides[slideIndex];
      if (slide) this.dataset.slideMediaType = slide.dataset.mediaType;
    }

  });
})();
